<template>
  <!-- Staff shell: full role-adaptive tabbed workspace. Staff drives the
       explicit condition; the customer shell is the fail-closed fallback. -->
  <template v-if="isStaffUser">
    <PageHeader
      :title="pageTitle"
      subtitle="Manage requirements and support tickets"
    >
      <template #bottom>
        <Page_Tabs
          :tabs="tabOptions"
          class="px-6 pt-4"
          no-border
        />
      </template>
    </PageHeader>

    <div :class="isFullBleed ? '' : 'p-6'">
      <div :class="isFullBleed ? '' : 'bg-white rounded border border-gray-200'">
        <Support_ServiceDesk
          v-if="activeTab === 'service-desk'"
          :selected-id="selectedId"
          :selected-title="selectedTicketTitle"
          @select="onSelect"
        />
        <Support_Activity v-else-if="activeTab === 'activity'" />
        <Support_Hades v-else-if="activeTab === 'hades'" />
        <Support_AppLatency v-else-if="activeTab === 'latency'" />
        <Support_Library
          v-else-if="activeTab === 'library'"
          :library-path="libraryPath"
          :library-tab="libraryTab"
          :library-view="libraryView"
          @title-change="setLibraryTitle"
        />
        <Support_List
          v-else
          :selected-id="selectedId"
          @select="onSelect"
        />
      </div>
    </div>
    <Support_UndoToast />
  </template>

  <!-- Customer shell: bare Service Desk, no internal chrome (absorbs the retired
       Support_Standalone). Fail-closed default for any non-staff user. -->
  <div v-else class="h-screen flex flex-col overflow-hidden">
    <Support_ServiceDesk
      :selected-id="selectedId"
      :selected-title="selectedTicketTitle"
      @select="onSelect"
    />
    <Support_UndoToast />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Support_List from './Support_List.vue';
import Support_ServiceDesk from './Support_ServiceDesk.vue';
import Support_Activity from './Support_Activity.vue';
import Support_Hades from './Hades/Support_Hades.vue';
import Support_AppLatency from './Latency/Support_AppLatency.vue';
import Support_Library from './Library/Support_Library.vue';
import Support_UndoToast from './Support_UndoToast.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import { COMPANY } from '@/constants';

// Requirements + Tickets merged into Work; Timeline is gone -- it was
// /api/requirements/ filtered to DONE_STATUSES ordered by -completed_at, which
// the merged list reproduces as Status = Done sorted by completed date. Activity
// survives because it reads /api/requirements/activity/, which returns events.
const TABS = [
  { label: 'Work', icon: ['fas', 'clipboard-list'], to: '/support/work' },
  { label: 'Activity', icon: ['fas', 'wave-square'], to: '/support/activity' },
  { label: 'Hades', icon: ['fas', 'magnifying-glass-chart'], to: '/support/hades' },
  { label: 'App Latency', icon: ['fas', 'gauge-high'], to: '/support/latency', superuserOnly: true },
  { label: 'Library', icon: ['fas', 'book-open'], to: '/support/library' },
  { label: 'Service Desk', icon: ['fas', 'headset'], to: '/support/service-desk' },
];

export default {
  name: 'Support_Overview',
  components: {
    Support_List,
    Support_ServiceDesk,
    Support_Activity,
    Support_Hades,
    Support_AppLatency,
    Support_Library,
    Support_UndoToast,
    PageHeader,
    Page_Tabs,
  },
  props: {
    selectedId: { type: [String, Number], default: null },
    libraryPath: { type: String, default: null },
    libraryTab: { type: String, default: null },
    libraryView: { type: String, default: null },
  },
  data() {
    return {
      libraryTitle: '',
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    // The drawer fetches the row into the store; the shell reads it from
    // there rather than issuing its own copy of the same request.
    selectedTicketTitle() {
      if (this.selectedId == null) return '';
      const row = this.$store.getters['supportItems/item'](Number(this.selectedId));
      return typeof row?.title === 'string' ? row.title : '';
    },
    isStaffUser() {
      // Kairon staff. Same predicate as Support_ServiceDesk / Support_ItemDetail
      // so the staff/customer split is consistent everywhere.
      return !!(this.current_user?.is_staff || this.current_user?.is_superuser);
    },
    isCustomerShell() {
      return !this.isStaffUser;
    },
    tabOptions() {
      return TABS.filter((tab) => !tab.superuserOnly || this.current_user?.is_superuser);
    },
    isFullBleed() {
      // Service Desk and Library manage their own header/toolbar and bleed to the edges.
      return this.activeTab === 'service-desk' || this.activeTab === 'library';
    },
    activeTab() {
      return this.$route.meta.supportTab || 'work';
    },
    pageTitle() {
      const section = this.activeTab === 'service-desk' ? 'Service Desk' : 'Support';
      if (this.selectedId == null) return section;
      const title = this.selectedTicketTitle.trim();
      return title
        ? `${section} #${this.selectedId} — ${title}`
        : `${section} #${this.selectedId}`;
    },
    browserTitle() {
      if (this.activeTab === 'work' || this.activeTab === 'service-desk') {
        const section = this.activeTab === 'service-desk' ? 'Service Desk' : 'Work';
        if (this.selectedId == null) return `${section} | ${COMPANY.NAME}`;
        const title = this.selectedTicketTitle.trim();
        const ticket = title ? `#${this.selectedId} - ${title}` : `#${this.selectedId}`;
        return `${ticket} | ${section} | ${COMPANY.NAME}`;
      }
      if (this.activeTab !== 'library') return `${this.pageTitle} | ${COMPANY.NAME}`;

      if (this.libraryView === 'runs') return `Library tests | ${COMPANY.NAME}`;
      if (!this.libraryPath || !this.libraryTitle) return `Library | ${COMPANY.NAME}`;
      const tabLabels = {
        overview: 'Overview',
        checks: 'Checks',
        history: 'History',
        code: 'Code',
        related: 'Related',
      };
      const tabTitle = tabLabels[this.libraryTab];
      const title = tabTitle ? `${this.libraryTitle} — ${tabTitle}` : this.libraryTitle;
      return `${title} | Library | ${COMPANY.NAME}`;
    },
  },
  watch: {
    $route() {
      this.ensureCustomerRoute();
      this.setBrowserTitle();
    },
    browserTitle: 'setBrowserTitle',
  },
  created() {
    this.ensureCustomerRoute();
  },
  mounted() {
    this.setBrowserTitle();
  },
  methods: {
    setLibraryTitle(title) {
      this.libraryTitle = typeof title === 'string' ? title : '';
    },
    setBrowserTitle() {
      document.title = this.browserTitle;
    },
    ensureCustomerRoute() {
      // A customer may hold MODULE_SERVICE_DESK (so the route guard admits them
      // to any /support/* path) but must only ever see the Service Desk. Keep
      // them there.
      if (this.isCustomerShell && this.activeTab !== 'service-desk') {
        this.$router.replace({ path: '/support/service-desk', query: this.$route.query });
      }
    },
    onSelect(id) {
      const bases = {
        'service-desk': '/support/service-desk',
        work: '/support/work',
      };
      const base = bases[this.activeTab] || bases.work;
      this.$router.push({
        path: id ? `${base}/${id}` : base,
        query: this.$route.query,
      });
    },
  },
};
</script>
